import React, { useEffect, useMemo } from "react";
import { Drawer, Box, Typography, Divider } from "@mui/material";
import { useAppDispatch, useAppSelector } from "@store/index";
import {
  chartConfigSelector,
  chartConfigDispatcher,
  updateViewerOptions,
} from "@store/slices/chart-config-slice";
import type { VisualizationBaseCondition } from "@models/VisualizationBase";
import {
  chartDrawerSelector,
  chartDrawerDispatcher,
} from "@store/slices/chart-drawer-slice";
import {
  useGetDatasetByIdQuery,
  useGetWeightConfigurationsQuery,
} from "@store/api/dataset-api";
import {
  buildDefaultDateGuardrails,
  type PointInTimeUnit,
  type GuardrailRollingOption,
  type ViewerSelectableTimePeriod,
  type PointInTimeViewerOptions,
} from "@models/Dataset";
// Reuse the bar chart's section components and logic hooks — grouped bar shares
// the same chart-config-slice, so the configuration + save logic is identical.
import DrawerHeader from "../bar-chart-config/components/DrawerHeader";
import OrientationSection from "../bar-chart-config/components/OrientationSection";
import FormattingSection from "../bar-chart-config/components/FormattingSection";
import DataSortingSection from "../bar-chart-config/components/DataSortingSection";
import TimePeriodSection from "../bar-chart-config/components/TimePeriodSection";
import StatisticalTestingSection from "../bar-chart-config/components/StatisticalTestingSection";
import DrawerFooter from "../bar-chart-config/components/DrawerFooter";
import BaseSection from "@components/charts/common/base-config/BaseSection";
import PointInTimeViewerOptionsSection from "../PointInTimeViewerOptionsSection";
import { useBarChartHandlers } from "../bar-chart-config/hooks/useBarChartHandlers";
import { useBarChartDerivedState } from "../bar-chart-config/hooks/useBarChartDerivedState";
import { useVisualizationBase } from "../bar-chart-config/hooks/useVisualizationBase";
import { useGroupedBarChartData } from "./hooks/useGroupedBarChartData";
import GroupBySection from "./components/GroupBySection";
import GroupedStylingSection from "./components/GroupedStylingSection";
import GroupedCategorySection from "./components/GroupedCategorySection";
import GroupedBarPreviewPanel from "./components/GroupedBarPreviewPanel";
import {
  getGroupedVisibleCounts,
  resolveGroupedSeriesColorEntries,
} from "@utils/grouped-bar-utils";

const GroupedBarChartConfigDrawer: React.FC = () => {
  const dispatch = useAppDispatch();
  const { currentConfig, datasetName, activeFilters, datasetId } =
    useAppSelector(chartConfigSelector);
  const { isDrawerOpen, editingWidgetId } =
    useAppSelector(chartDrawerSelector);

  const { data: datasetDetails } = useGetDatasetByIdQuery(datasetId as string, {
    skip: !datasetId,
  });

  const { data: weightConfigData } = useGetWeightConfigurationsQuery(
    datasetId as string,
    { skip: !datasetId },
  );

  // ─── Point-in-Time Date Guardrails (same as bar chart) ────────────────────
  const pitGuardrails = useMemo(() => {
    return (
      datasetDetails?.dateGuardrails?.pointInTime ??
      buildDefaultDateGuardrails().pointInTime
    );
  }, [datasetDetails?.dateGuardrails]);

  const currentPitUnit = useMemo((): PointInTimeUnit | null => {
    const tp = currentConfig.timePeriodSelection?.timePeriod;
    if (tp === "latest_quarter" || tp === "previous_quarter") return "quarter";
    if (tp === "latest_semester" || tp === "previous_semester")
      return "semester";
    return null;
  }, [currentConfig.timePeriodSelection?.timePeriod]);

  const pitUnitGuardrail = currentPitUnit
    ? pitGuardrails.units[currentPitUnit]
    : null;

  const pitGuardrailAllowedWindowSizes: number[] | undefined =
    pitUnitGuardrail?.allowedRollingAverages.filter((n) => n !== 0);

  const pitGuardrailNoRollingDisabled: boolean = pitUnitGuardrail
    ? !pitUnitGuardrail.allowedRollingAverages.includes(
        0 as GuardrailRollingOption,
      )
    : false;

  const pitGuardrailRollingDisabled: boolean =
    !!pitUnitGuardrail && (pitGuardrailAllowedWindowSizes?.length ?? 0) === 0;

  const pitGuardrailConflict = useMemo((): string | null => {
    if (!pitUnitGuardrail || !currentPitUnit) return null;
    const isRollingEnabled = currentConfig.rollingAverage?.enabled ?? false;
    const windowSize = (currentConfig.rollingAverage?.windowSize ??
      0) as GuardrailRollingOption;
    const effectiveOption: GuardrailRollingOption = isRollingEnabled
      ? windowSize
      : 0;
    if (!pitUnitGuardrail.allowedRollingAverages.includes(effectiveOption)) {
      return isRollingEnabled
        ? `Rolling average of ${windowSize} periods is no longer allowed for ${currentPitUnit} by dataset policy.`
        : `No Rolling Average is no longer allowed for ${currentPitUnit} by dataset policy.`;
    }
    return null;
  }, [pitUnitGuardrail, currentPitUnit, currentConfig.rollingAverage]);

  const { closeChartDrawer } = chartDrawerDispatcher(dispatch);
  const {
    updateOrientation,
    updatePercentageFormatting,
    updateValuePlacement,
    updateDataSorting,
    updateTopNResponses,
    updateCombineRemainingIntoOther,
    updateStatTestingEnabled,
    updateStatTestType,
    updateSignificanceLevel,
    updateMinimumSampleSize,
    updateChartTitle,
    updateChartSubtitle,
    updateTitleBold,
    updateTitleUnderline,
    setGroupedColorPalette,
    setGroupedSeriesColor,
    clearGroupedSeriesColors,
    updateCompactMode,
  } = chartConfigDispatcher(dispatch);

  const { fetchChartData, isRefetchingData, weightsApplied, setWeightsApplied } =
    useGroupedBarChartData();

  const {
    localApplyFilters,
    isTogglingFilters,
    debouncedApplyFilters,
    usePreviewDateFilter,
    handleFilterToggle,
    handlePreviewDateFilterToggle,
    handleWeightChange,
    handleTimePeriodChange,
    handleTimePeriodDatesChange,
    handleStatTestingChange,
    handleRollingAverageEnabledChange,
    handleRollingAverageWindowSizeChange,
    handleSave,
    handleDiscard,
    isCreatingChart,
    updateFocusParams,
  } = useBarChartHandlers({
    fetchChartData,
    isRefetchingData,
    setWeightsApplied,
    pitGuardrails,
  });

  useEffect(() => {
    if (
      editingWidgetId === "new-widget" &&
      (weightConfigData?.weightConfigurations?.length ?? 0) > 0 &&
      currentConfig.weight === "DISABLED"
    ) {
      handleWeightChange("DATASET_DEFAULT");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingWidgetId, weightConfigData]);

  const {
    questions,
    isAvailableData,
    canSave,
    headerTitle,
    isAllTimePeriod,
  } = useBarChartDerivedState(
    isRefetchingData,
    datasetDetails?.dataAvailabilityDate,
  );

  const isDisabled = isRefetchingData || isTogglingFilters;

  const {
    base: visualizationBase,
    isValid: isVisualizationBaseValid,
    enableCustomBase: enableVisualizationBase,
    disableCustomBase: disableVisualizationBase,
    updateName: updateVisualizationBaseName,
    saveCondition: saveVisualizationBaseCondition,
    removeCondition: removeVisualizationBaseCondition,
  } = useVisualizationBase();

  // The backend's focusMode is inverted relative to the UI grouping label:
  // focusMode VALUE puts variables on the x-axis (the "Variables" grouping) and
  // VARIABLE puts values on the x-axis (the "Values" grouping). Map accordingly.
  const groupBy = useMemo((): "variable" | "value" => {
    return currentConfig.focusMode?.toLowerCase() === "variable"
      ? "value"
      : "variable";
  }, [currentConfig.focusMode]);

  // Require a real grouped comparison: at least one visible group and at least
  // two visible series.
  const isGroupedSelectionValid = useMemo(() => {
    const { groups, series } = getGroupedVisibleCounts(
      currentConfig.groupedBar,
      groupBy,
    );
    return groups >= 1 && series >= 2;
  }, [currentConfig.groupedBar, groupBy]);

  // Visible series entries (with display labels) for the Custom-palette swatches.
  const seriesColorEntries = useMemo(
    () => resolveGroupedSeriesColorEntries(currentConfig.groupedBar, groupBy),
    [currentConfig.groupedBar, groupBy],
  );

  // Group By change → update focusMode and refetch with GROUPED_BAR.
  const handleGroupByChange = (level: "variable" | "value") => {
    const upperLevel = level === "variable" ? "VALUE" : "VARIABLE";
    updateFocusParams({ focusMode: upperLevel, focusEntity: "" });
    // The series dimension swaps, so per-series manual colors no longer apply.
    clearGroupedSeriesColors();
    fetchChartData(
      currentConfig.weight,
      debouncedApplyFilters,
      undefined,
      undefined,
      undefined,
      false,
      undefined,
      { focusMode: upperLevel, focusEntity: "" },
    );
  };

  // ─── Base changes refetch immediately (same pattern as bar chart) ─────────
  const handleEnableVisualizationBase = () => {
    enableVisualizationBase();
    const updatedBase = { ...visualizationBase, enabled: true };
    fetchChartData(currentConfig.weight, debouncedApplyFilters, undefined, undefined, undefined, false, undefined, undefined, undefined, updatedBase);
  };

  const handleDisableVisualizationBase = () => {
    disableVisualizationBase();
    const updatedBase = { ...visualizationBase, enabled: false };
    fetchChartData(currentConfig.weight, debouncedApplyFilters, undefined, undefined, undefined, false, undefined, undefined, undefined, updatedBase);
  };

  const handleSaveVisualizationBaseCondition = (
    condition: VisualizationBaseCondition,
    isEditing: boolean,
  ) => {
    saveVisualizationBaseCondition(condition, isEditing);
    const updatedConditions = isEditing
      ? visualizationBase.conditions.map((c) =>
          c.id === condition.id ? condition : c,
        )
      : [...visualizationBase.conditions, condition];
    const updatedBase = { ...visualizationBase, conditions: updatedConditions };
    fetchChartData(currentConfig.weight, debouncedApplyFilters, undefined, undefined, undefined, false, undefined, undefined, undefined, updatedBase);
  };

  const handleRemoveVisualizationBaseCondition = (id: string) => {
    removeVisualizationBaseCondition(id);
    const updatedBase = {
      ...visualizationBase,
      conditions: visualizationBase.conditions.filter((c) => c.id !== id),
    };
    fetchChartData(currentConfig.weight, debouncedApplyFilters, undefined, undefined, undefined, false, undefined, undefined, undefined, updatedBase);
  };

  const isViewerOptionsInvalid =
    currentPitUnit !== null &&
    currentConfig.viewerOptions.enabled &&
    Object.keys(currentConfig.viewerOptions.additionalPeriods).length === 0 &&
    currentConfig.viewerOptions.chartPeriodAdditionalRollingAverages.length ===
      0;

  return (
    <Drawer
      anchor="right"
      open={isDrawerOpen}
      onClose={() => closeChartDrawer()}
      sx={{ "& .MuiDrawer-paper": { width: "80vw", boxSizing: "border-box" } }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <DrawerHeader
          questionLabel={questions?.label}
          headerTitle={headerTitle}
          datasetName={datasetName}
          activeFiltersCount={activeFilters.length}
          localApplyFilters={localApplyFilters}
          isTogglingFilters={isTogglingFilters}
          isRefetchingData={isRefetchingData}
          onFilterToggle={handleFilterToggle}
          usePreviewDateFilter={usePreviewDateFilter}
          onPreviewDateFilterToggle={handlePreviewDateFilterToggle}
        />

        <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
          {/* Configuration Panel */}
          <Box
            sx={{
              width: 400,
              borderRight: 1,
              borderColor: "divider",
              overflow: "auto",
              p: 3,
            }}
          >
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>
              Configurations
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <GroupBySection
                groupBy={groupBy}
                isDisabled={isDisabled}
                onGroupByChange={handleGroupByChange}
              />

              <GroupedCategorySection
                groupBy={groupBy}
                isDisabled={isDisabled}
              />

              <OrientationSection
                orientation={currentConfig.orientation}
                onOrientationChange={updateOrientation}
              />

              <FormattingSection
                percentageFormatting={currentConfig.percentageFormatting}
                valuePlacement={currentConfig.valuePlacement}
                weight={currentConfig.weight}
                weightsApplied={weightsApplied}
                isRefetchingData={isRefetchingData}
                onPercentageFormattingChange={updatePercentageFormatting}
                onValuePlacementChange={updateValuePlacement}
                onWeightChange={handleWeightChange}
              />

              <DataSortingSection
                dataSorting={currentConfig.dataSorting}
                topNResponses={currentConfig.topNResponses}
                topNValue={currentConfig.topNValue}
                combineRemainingIntoOther={
                  currentConfig.combineRemainingIntoOther
                }
                onDataSortingChange={updateDataSorting}
                onTopNResponsesChange={updateTopNResponses}
                onCombineRemainingChange={updateCombineRemainingIntoOther}
              />

              <Divider />

              <BaseSection
                datasetId={datasetId}
                isDisabled={isDisabled}
                base={visualizationBase}
                onEnableCustomBase={handleEnableVisualizationBase}
                onDisableCustomBase={handleDisableVisualizationBase}
                onNameChange={updateVisualizationBaseName}
                onSaveCondition={handleSaveVisualizationBaseCondition}
                onRemoveCondition={handleRemoveVisualizationBaseCondition}
              />

              <Divider />

              <TimePeriodSection
                timePeriodSelection={currentConfig.timePeriodSelection}
                rollingAverage={currentConfig.rollingAverage}
                statisticalTestingEnabled={
                  currentConfig.statisticalTesting?.enabled || false
                }
                responsesMinDate={currentConfig.responsesMinDate}
                responsesMaxDate={currentConfig.responsesMaxDate}
                isDisabled={isDisabled}
                totalAvailablePeriods={currentConfig.categories?.length}
                onTimePeriodChange={handleTimePeriodChange}
                onTimePeriodDatesChange={handleTimePeriodDatesChange}
                onRollingAverageEnabledChange={handleRollingAverageEnabledChange}
                onRollingAverageWindowSizeChange={
                  handleRollingAverageWindowSizeChange
                }
                guardrailAllowedWindowSizes={pitGuardrailAllowedWindowSizes}
                guardrailNoRollingDisabled={pitGuardrailNoRollingDisabled}
                guardrailRollingDisabled={pitGuardrailRollingDisabled}
                guardrailConflictWarning={pitGuardrailConflict ?? undefined}
              />

              {currentPitUnit && (
                <>
                  <Divider />
                  <PointInTimeViewerOptionsSection
                    viewerOptions={currentConfig.viewerOptions}
                    chartTimePeriod={
                      currentConfig.timePeriodSelection
                        ?.timePeriod as ViewerSelectableTimePeriod
                    }
                    chartRollingWindowSize={
                      currentConfig.rollingAverage?.enabled
                        ? (currentConfig.rollingAverage?.windowSize ?? 0)
                        : 0
                    }
                    pitGuardrailUnits={pitGuardrails.units}
                    isDisabled={isDisabled}
                    onViewerOptionsChange={(opts: PointInTimeViewerOptions) =>
                      dispatch(updateViewerOptions(opts))
                    }
                    datasetId={datasetId}
                    onRestoreSharedTime={(config) => {
                      const d = chartConfigDispatcher(dispatch);
                      d.updateTimePeriod(
                        config.savedDefault as typeof currentConfig.timePeriodSelection.timePeriod,
                      );
                      const ra = config.savedRollingAverage;
                      d.updateRollingAverageEnabled(ra > 0);
                      if (ra > 0) d.updateRollingAverageWindowSize(ra);
                      if (config.viewerOptions) {
                        dispatch(
                          updateViewerOptions(
                            config.viewerOptions as typeof currentConfig.viewerOptions,
                          ),
                        );
                      }
                    }}
                  />
                </>
              )}

              <Divider />

              <StatisticalTestingSection
                statisticalTesting={currentConfig.statisticalTesting}
                isDisabled={isDisabled}
                isAllTimePeriod={isAllTimePeriod}
                rollingAverageEnabled={
                  currentConfig.rollingAverage?.enabled || false
                }
                onStatTestingChange={handleStatTestingChange}
                updateStatTestingEnabled={updateStatTestingEnabled}
                updateStatTestType={updateStatTestType}
                updateSignificanceLevel={updateSignificanceLevel}
                updateMinimumSampleSize={updateMinimumSampleSize}
              />

              <Divider />

              <GroupedStylingSection
                title={currentConfig.title}
                subtitle={currentConfig.subtitle}
                titleBold={currentConfig.titleBold}
                titleUnderline={currentConfig.titleUnderline}
                colorPalette={
                  currentConfig.groupedBar?.colorPalette ?? "Multicolor"
                }
                seriesColorEntries={seriesColorEntries}
                manualColors={
                  currentConfig.groupedBar?.seriesManualColors ?? {}
                }
                compactMode={currentConfig.compactMode}
                onTitleChange={updateChartTitle}
                onSubtitleChange={updateChartSubtitle}
                onTitleBoldChange={updateTitleBold}
                onTitleUnderlineChange={updateTitleUnderline}
                onColorPaletteChange={setGroupedColorPalette}
                onSeriesColorChange={(id, color) =>
                  setGroupedSeriesColor({ id, color })
                }
                onCompactModeChange={updateCompactMode}
              />
            </Box>
          </Box>

          {/* Preview Panel */}
          <GroupedBarPreviewPanel
            currentConfig={currentConfig}
            isRefetchingData={isRefetchingData}
            isAvailableData={isAvailableData}
          />
        </Box>

        <DrawerFooter
          isCreatingChart={isCreatingChart}
          canSave={
            canSave &&
            !pitGuardrailConflict &&
            !isViewerOptionsInvalid &&
            isVisualizationBaseValid &&
            isGroupedSelectionValid
          }
          isRefetchingData={isRefetchingData}
          onSave={handleSave}
          onDiscard={handleDiscard}
        />
      </Box>
    </Drawer>
  );
};

export default GroupedBarChartConfigDrawer;
